// src/tabs/messages/InboxThread.jsx — Phase 2.3 (ADR-026)
// Vue d'un thread : header (voyageur + résa + logement) + messages
// chronologiques (bubbles inbound gauche, outbound droite) + composer
// au pied de page (Phase 2.3 commit 5).
//
// `readOnly` (Permissions 42 Phase 2 — espace proprio) : masque l'InboxComposer
// (la réponse reste une action ADMIN). Défaut false → comportement admin
// inchangé. Le proprio (co-hôte) consulte ses conversations mais n'envoie jamais.

import { Home, CalendarDays, Eye } from "lucide-react"
import { DS } from "../../lib/ui"
import InboxComposer, { isPlatformSource } from "./InboxComposer"
import ThreadDisplay from "./ThreadDisplay"
import GuestTimesPills from "../../components/GuestTimesPills"  // Heures voyageur (formulaire de bienvenue) — ADMIN, ADR-226

export default function InboxThread({ thread, reservations, logements, onComposerSent, readOnly = false }) {
  const threadMsgs = thread.messages || []
  const lastMsg = threadMsgs[threadMsgs.length - 1]
  const reservation = lastMsg?.reservation_id ? reservations[lastMsg.reservation_id] : null
  const logement = reservation?.logement_id ? logements.find(l => l.id === reservation.logement_id) : null

  // BUG 38 — multi-canal : on peut répondre dès qu'au moins un canal activé
  // dispose de sa coordonnée (email OU tchat_ota). Le choix des canaux est géré
  // par InboxComposer (cases à cocher) ; ici on ne fait que gater l'affichage.
  // Gating plateforme : résa airbnb/booking → seul le Chat OTA est proposé par
  // le composer, donc le fil n'est joignable QUE via beds24_booking_id (l'alias
  // email OTA ne compte plus). Direct / autre : critères inchangés.
  const canReply = isPlatformSource(reservation?.source)
    ? Boolean(reservation?.beds24_booking_id)
    : Boolean(reservation?.guest_email) || Boolean(reservation?.beds24_booking_id)

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",minHeight:480,maxHeight:"calc(100vh - 260px)"}}>
      {/* Header thread */}
      <div style={{padding:"12px 16px",borderBottom:`1px solid ${DS.border}`,background:DS.card}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",gap:10,flexWrap:"wrap"}}>
          <div>
            <div style={{fontSize:14.5,fontWeight:600,color:DS.marineDark,fontFamily:DS.fontDisplay}}>
              {reservation
                ? `${reservation.guest_first_name || '?'} ${reservation.guest_last_name || ''}`.trim()
                : (lastMsg?.sender_name || lastMsg?.sender_email || 'Inconnu')}
            </div>
            <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap",fontSize:11,color:DS.textSecondary,marginTop:3}}>
              {reservation?.guest_email && <span>{reservation.guest_email}</span>}
              {logement && <span style={{display:"inline-flex",alignItems:"center",gap:3}}>· <Home size={12} strokeWidth={2}/> {logement.id} {logement.nom}</span>}
              {reservation?.start_date && <span style={{display:"inline-flex",alignItems:"center",gap:3}}>· <CalendarDays size={12} strokeWidth={2}/> {reservation.start_date} → {reservation.end_date}</span>}
              {reservation?.source && <span>· {reservation.source}</span>}
            </div>
            {/* Heures voyageur (formulaire de bienvenue, dernière soumission).
                ⚠️ ADR-226 : composant PARTAGÉ avec l'espace proprio (readOnly)
                → gate `!readOnly` OBLIGATOIRE, le proprio ne voit jamais les
                heures réelles (défense en profondeur : les endpoints proprio_*
                ne SELECTent de toute façon jamais guest_arrival/departure_time,
                cf. proprio-rls-invariants). Rien rendu si non renseignées. */}
            {!readOnly && (
              <GuestTimesPills
                arrival={reservation?.guest_arrival_time}
                departure={reservation?.guest_departure_time}
                style={{ marginTop: 5 }}
              />
            )}
          </div>
          <span style={{fontSize:10,color:DS.textMuted,fontFamily:'monospace',padding:"2px 6px",background:DS.pageBg,borderRadius:5}}>{thread.thread_id}</span>
        </div>
      </div>

      {/* Messages chronologiques (extrait Phase 2.G.4 dans ThreadDisplay réutilisable) */}
      <ThreadDisplay messages={threadMsgs} emptyMessage="Aucun message dans ce thread." />

      {/* Composer (Phase 2.3 commit 5 → BUG 38 multi-canal). MASQUÉ en lecture
          seule (espace proprio co-hôte : consultation, l'envoi reste admin). */}
      {readOnly ? (
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:6,padding:"10px 16px",borderTop:`1px solid ${DS.border}`,background:DS.card,fontSize:11,color:DS.textMuted,fontStyle:"italic"}}>
          <Eye size={13} strokeWidth={2}/> Consultation en lecture seule
        </div>
      ) : (
        <InboxComposer
          threadId={thread.thread_id}
          reservation={reservation}
          canReply={canReply}
          lastInbound={lastInboundMessage(threadMsgs)}
          onSent={onComposerSent}
        />
      )}
    </div>
  )
}

// Lot « conversation enrichie » — GARDE (b) : une entrée canal='form_comment'
// (commentaire du formulaire de bienvenue) n'est JAMAIS « répondable » comme un
// message plateforme — elle ne devient pas le lastInbound du composer (sinon
// présélection de canal / threading email dérivés d'une entrée qui n'est pas
// un message OTA/mail). Même règle pour canal='depart_feedback' (ressenti de
// fin de séjour, système d'avis « option a ») : ce n'est pas un message
// plateforme, la réponse part comme un message NEUF. Exporté pour test.
const NON_REPONDABLE_CANAUX = new Set(['form_comment', 'depart_feedback'])
export function lastInboundMessage(messages) {
  const msgs = messages || []
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].direction === 'inbound' && !NON_REPONDABLE_CANAUX.has(msgs[i].canal)) return msgs[i]
  }
  return null
}
